import { useState, useRef, useCallback, useEffect, type RefObject } from "react";

type RecordingState = "idle" | "recording" | "stopped";

interface CanvasRecorderOptions {
  mirrorReference?: boolean;
  mirrorCamera?: boolean;
  fps?: number;
  canvasWidth?: number;
  canvasHeight?: number;
}

export function useCanvasRecorder(
  referenceVideoRef: RefObject<HTMLVideoElement | null>,
  cameraVideoRef: RefObject<HTMLVideoElement | null>,
  options: CanvasRecorderOptions = {}
) {
  const {
    mirrorReference = false,
    mirrorCamera = false,
    fps = 30,
    canvasWidth = 1280,
    canvasHeight = 360,
  } = options;

  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const rafRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Keep mirror options in refs so the RAF loop always reads the latest values
  const mirrorRefRef = useRef(mirrorReference);
  const mirrorCamRef = useRef(mirrorCamera);
  useEffect(() => { mirrorRefRef.current = mirrorReference; }, [mirrorReference]);
  useEffect(() => { mirrorCamRef.current = mirrorCamera; }, [mirrorCamera]);

  const drawFrame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const halfW = canvasWidth / 2;
    const h = canvasHeight;

    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, canvasWidth, h);

    const drawHalf = (
      el: HTMLVideoElement | null,
      destX: number,
      mirror: boolean
    ) => {
      if (!el || el.readyState < 2) return;
      ctx.save();
      if (mirror) {
        ctx.translate(destX + halfW, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(el, 0, 0, halfW, h);
      } else {
        ctx.drawImage(el, destX, 0, halfW, h);
      }
      ctx.restore();
    };

    drawHalf(referenceVideoRef.current, 0, mirrorRefRef.current);
    drawHalf(cameraVideoRef.current, halfW, mirrorCamRef.current);

    rafRef.current = requestAnimationFrame(drawFrame);
  }, [referenceVideoRef, cameraVideoRef, canvasWidth, canvasHeight]);

  const startRecording = useCallback(() => {
    // Create offscreen canvas
    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    canvasRef.current = canvas;

    chunksRef.current = [];

    // Start drawing loop
    rafRef.current = requestAnimationFrame(drawFrame);

    // Capture canvas stream
    const stream = canvas.captureStream(fps);
    streamRef.current = stream;

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : "video/webm";

    const mr = new MediaRecorder(stream, { mimeType });
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setRecordingBlob(blob);
      setRecordingUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
      setRecordingState("stopped");
    };
    mr.start();
    mediaRecorderRef.current = mr;
    setRecordingState("recording");
  }, [drawFrame, fps, canvasWidth, canvasHeight]);

  const stopRecording = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    streamRef.current?.getTracks().forEach(t => t.stop());
    mediaRecorderRef.current?.stop();
  }, []);

  const discardRecording = useCallback(() => {
    setRecordingUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setRecordingBlob(null);
    setRecordingState("idle");
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
      setRecordingUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, []);

  return { recordingState, recordingUrl, recordingBlob, startRecording, stopRecording, discardRecording };
}
